/**
 * Local fetch-based service for the XEPPT Webhook Management API.
 * Does NOT use the xeppt-sdk — these endpoints are not in the published SDK.
 *
 * AUTH NOTE: Calls the JWT-authed portal proxy at /account/webhooks/* (Bearer
 * token from localStorage 'client-metadata', same as connect_service.ts).
 * The proxy is wire-compatible with the /v1/webhooks/* DTOs — types are unchanged.
 *
 * ENVIRONMENT: Every list/create/delete/rotate/stats call takes an explicit
 * `environment` argument (from the dev-portal's Sandbox/Live pill, via
 * useEnvironmentContext) and sends it as ?environment=<value>. There is no
 * hardcoded default here — callers must always pass the selected environment.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

export type WebhookEnvironment = 'test' | 'live';

function authHeaders(): Record<string, string> {
    try {
        const raw = localStorage.getItem('client-metadata');
        if (!raw) return {};
        const meta = JSON.parse(raw) as { accessToken?: string };
        return meta?.accessToken ? { Authorization: `Bearer ${meta.accessToken}` } : {};
    } catch {
        return {};
    }
}

// ─── Types (mirror Go DTOs in webhook_management.go) ────────────────────────

export interface WebhookEndpointItem {
    id: string;
    url: string;
    events: string[];
    description?: string;
    environment: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface CreateWebhookEndpointRequest {
    url: string;
    events: string[];
    description?: string;
}

/** signing_secret is present ONLY in the create response — never in list or detail. */
export interface CreateWebhookEndpointResponse {
    id: string;
    url: string;
    events: string[];
    description?: string;
    environment: string;
    signing_secret: string;
    created_at: string;
}

export interface ListWebhookEndpointsResponse {
    endpoints: WebhookEndpointItem[];
}

export interface WebhookDeliverySummary {
    id: string;
    endpoint_id: string;
    event_type: string;
    status: string;
    attempts: number;
    max_attempts: number;
    response_status?: number;
    last_error?: string;
    next_attempt_at?: string;
    delivered_at?: string;
    created_at: string;
}

export interface ListWebhookDeliveriesOutput {
    deliveries: WebhookDeliverySummary[];
    has_more: boolean;
    next_cursor?: string;
}

export interface GetWebhookDeliveryOutput {
    id: string;
    endpoint_id: string;
    event_type: string;
    status: string;
    /** Raw JSON payload posted to the merchant endpoint. */
    payload: unknown;
    attempts: number;
    max_attempts: number;
    response_status?: number;
    last_error?: string;
    next_attempt_at: string;
    order_id?: string;
    delivered_at?: string;
    created_at: string;
    updated_at: string;
}

/** Per-endpoint (or combined-totals) delivery stats. Rates/latency are null when
 *  the window has no terminal / no delivered deliveries (render "—", not 0). */
export interface WebhookEndpointDeliveryStats {
    /** Absent on the combined `totals` object. */
    endpoint_id?: string;
    total: number;
    delivered: number;
    failed: number;
    pending: number;
    retried: number;
    /** delivered / (delivered + failed), 0..1; null when no terminal deliveries. */
    success_rate: number | null;
    /** mean create→deliver latency (ms); null when nothing was delivered. */
    avg_latency_ms: number | null;
    last_delivery_at?: string;
}

export interface GetWebhookDeliveryStatsOutput {
    window_hours: number;
    totals: WebhookEndpointDeliveryStats;
    endpoints: WebhookEndpointDeliveryStats[];
}

export interface WebhookEventType {
    name: string;
    description: string;
    /** "available" | "reserved" */
    status: string;
}

export interface ListWebhookEventTypesOutput {
    event_types: WebhookEventType[];
}

/** Response from POST …/rotate-secret — the new secret is returned ONCE only. */
export interface RotateWebhookSecretResponse {
    id: string;
    signing_secret: string;
}

// ─── Error class ────────────────────────────────────────────────────────────

export class WebhookApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly code?: string,
        options?: ErrorOptions
    ) {
        super(message, options);
        this.name = 'WebhookApiError';
    }
}

async function throwOnError(response: Response): Promise<never> {
    let code: string | undefined;
    let message: string | undefined;
    try {
        const errBody = (await response.json()) as { code?: string; message?: string };
        code = typeof errBody?.code === 'string' ? errBody.code : undefined;
        message = typeof errBody?.message === 'string' ? errBody.message : undefined;
    } catch {
        // ignore JSON parse failures on error bodies
    }
    throw new WebhookApiError(
        message ?? `HTTP ${response.status}`,
        response.status,
        code
    );
}

async function parseResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        await throwOnError(response);
    }
    return response.json() as Promise<T>;
}

// ─── Service ────────────────────────────────────────────────────────────────

export const apiWebhookService = {
    /** GET /account/webhooks/endpoints — lists all endpoints for the authenticated merchant. */
    async listEndpoints(environment: WebhookEnvironment): Promise<ListWebhookEndpointsResponse> {
        const response = await fetch(
            `${BACKEND_URL}/account/webhooks/endpoints?environment=${environment}`,
            { headers: authHeaders() }
        );
        return parseResponse<ListWebhookEndpointsResponse>(response);
    },

    /**
     * POST /account/webhooks/endpoints — creates a new endpoint.
     * The signing_secret in the response is returned ONCE only.
     */
    async createEndpoint(
        req: CreateWebhookEndpointRequest,
        environment: WebhookEnvironment
    ): Promise<CreateWebhookEndpointResponse> {
        const response = await fetch(
            `${BACKEND_URL}/account/webhooks/endpoints?environment=${environment}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(req)
            }
        );
        return parseResponse<CreateWebhookEndpointResponse>(response);
    },

    /** GET /account/webhooks/endpoints/{id} — single endpoint detail (no signing_secret). */
    async getEndpoint(id: string, environment: WebhookEnvironment): Promise<WebhookEndpointItem> {
        const response = await fetch(
            `${BACKEND_URL}/account/webhooks/endpoints/${id}?environment=${environment}`,
            { headers: authHeaders() }
        );
        return parseResponse<WebhookEndpointItem>(response);
    },

    /** DELETE /account/webhooks/endpoints/{id} — soft-deletes the endpoint. */
    async deleteEndpoint(id: string, environment: WebhookEnvironment): Promise<void> {
        const response = await fetch(
            `${BACKEND_URL}/account/webhooks/endpoints/${id}?environment=${environment}`,
            {
                method: 'DELETE',
                headers: authHeaders()
            }
        );
        if (!response.ok) {
            await throwOnError(response);
        }
    },

    /**
     * GET /account/webhooks/deliveries — cursor-paginated delivery list.
     * Pass after=<next_cursor> from a previous response for the next page.
     */
    async listDeliveries(params: {
        environment: WebhookEnvironment;
        after?: string;
        limit?: number;
    }): Promise<ListWebhookDeliveriesOutput> {
        const qs = new URLSearchParams();
        qs.set('environment', params.environment);
        if (params.after) qs.set('after', params.after);
        if (params.limit != null) qs.set('limit', String(params.limit));
        const response = await fetch(
            `${BACKEND_URL}/account/webhooks/deliveries?${qs.toString()}`,
            { headers: authHeaders() }
        );
        return parseResponse<ListWebhookDeliveriesOutput>(response);
    },

    /**
     * GET /account/webhooks/stats — per-endpoint delivery observability stats
     * (success rate, retried/dead-letter counts, average latency) over a trailing
     * window, plus a combined `totals`.  windowHours defaults to 24 server-side.
     */
    async getStats(params: {
        environment: WebhookEnvironment;
        windowHours?: number;
    }): Promise<GetWebhookDeliveryStatsOutput> {
        const qs = new URLSearchParams();
        qs.set('environment', params.environment);
        if (params.windowHours != null) qs.set('window_hours', String(params.windowHours));
        const response = await fetch(`${BACKEND_URL}/account/webhooks/stats?${qs.toString()}`, {
            headers: authHeaders()
        });
        return parseResponse<GetWebhookDeliveryStatsOutput>(response);
    },

    /** GET /account/webhooks/deliveries/{id} — full delivery detail including payload. */
    async getDelivery(id: string): Promise<GetWebhookDeliveryOutput> {
        const response = await fetch(`${BACKEND_URL}/account/webhooks/deliveries/${id}`, {
            headers: authHeaders()
        });
        return parseResponse<GetWebhookDeliveryOutput>(response);
    },

    /**
     * POST /account/webhooks/deliveries/{id}/replay — re-enqueues the delivery as a
     * new pending row (new whd_ ID).  Returns the new delivery in GetWebhookDeliveryOutput
     * shape.  Backend returns 201 Created.
     */
    async replayDelivery(id: string): Promise<GetWebhookDeliveryOutput> {
        const response = await fetch(
            `${BACKEND_URL}/account/webhooks/deliveries/${id}/replay`,
            {
                method: 'POST',
                headers: authHeaders()
            }
        );
        return parseResponse<GetWebhookDeliveryOutput>(response);
    },

    /**
     * POST /account/webhooks/endpoints/{id}/test-event — enqueues a single synthetic
     * delivery to the endpoint.  eventType is optional; the backend picks a sensible
     * default when omitted.  Returns the enqueued delivery in GetWebhookDeliveryOutput
     * shape.  Backend returns 201 Created.
     */
    async sendTestEvent(id: string, eventType?: string): Promise<GetWebhookDeliveryOutput> {
        const body = eventType ? JSON.stringify({ event_type: eventType }) : undefined;
        const response = await fetch(
            `${BACKEND_URL}/account/webhooks/endpoints/${id}/test-event`,
            {
                method: 'POST',
                headers: {
                    ...(body ? { 'Content-Type': 'application/json' } : {}),
                    ...authHeaders()
                },
                body
            }
        );
        return parseResponse<GetWebhookDeliveryOutput>(response);
    },

    /**
     * POST /account/webhooks/endpoints/{id}/rotate-secret — generates a new signing
     * secret for the endpoint.  The new secret is returned ONCE in the response;
     * the previous secret remains valid for 24 hours (zero-downtime rotation).
     */
    async rotateSecret(
        id: string,
        environment: WebhookEnvironment
    ): Promise<RotateWebhookSecretResponse> {
        const response = await fetch(
            `${BACKEND_URL}/account/webhooks/endpoints/${id}/rotate-secret?environment=${environment}`,
            {
                method: 'POST',
                headers: authHeaders()
            }
        );
        return parseResponse<RotateWebhookSecretResponse>(response);
    },

    /**
     * GET /account/webhooks/event-types — event type catalog.
     * Filter to status === "available" when building subscription selectors.
     */
    async listEventTypes(environment: WebhookEnvironment): Promise<ListWebhookEventTypesOutput> {
        const response = await fetch(
            `${BACKEND_URL}/account/webhooks/event-types?environment=${environment}`,
            { headers: authHeaders() }
        );
        return parseResponse<ListWebhookEventTypesOutput>(response);
    }
};
